import { clsx } from 'clsx';
import { useMasterDocumentTranslation } from './i18n';
import type { FactBoxFinancialProps } from './MasterDocument.types';

function Row({
  label,
  value,
  color,
  bold,
}: {
  label: string;
  value: string;
  color?: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <span
        className={clsx(
          'text-xs',
          bold
            ? 'font-bold text-content-primary dark:text-dark-content-primary'
            : 'text-content-tertiary dark:text-dark-content-secondary',
        )}
      >
        {label}
      </span>
      <span
        className={clsx(
          'text-xs tabular-nums',
          bold ? 'font-bold' : 'font-semibold',
          color ? color : 'text-content-primary dark:text-dark-content-primary',
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Widget de movimientos financieros para el panel FactBox.
 *
 * Presenta cuentas por cobrar, cuentas por pagar, anticipos cruzados y efectivo
 * en una lista de filas etiqueta/valor. Cuando el documento opera en moneda
 * extranjera, muestra un bloque adicional con los equivalentes en moneda local
 * (COP). Si no existe ningún movimiento registrado, renderiza un mensaje de
 * "Pendiente de contabilización".
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - FactBoxFinancialProps
 *
 * @example
 * ```tsx
 * <FactBoxFinancial
 *   data={{ receivableAmount: 1500000 }}
 *   formatCurrency={formatCurrency}   // de useMasterDocumentFormatters()
 * />
 * ```
 */
export function FactBoxFinancial({ data, formatCurrency, className }: FactBoxFinancialProps) {
  const { t } = useMasterDocumentTranslation();
  const hasReceivable = data.receivableAmount != null && data.receivableAmount !== 0;
  const hasPayable = data.payableAmount != null && data.payableAmount !== 0;
  const hasAdvance = data.advanceAmount != null && data.advanceAmount !== 0;
  const hasCash = data.cashAmount != null && data.cashAmount !== 0;
  const hasLocal = data.receivableAmountLocal != null || data.payableAmountLocal != null;

  const isEmpty = !hasReceivable && !hasPayable && !hasAdvance && !hasCash;

  if (isEmpty) {
    return (
      <div className={clsx('text-xs text-content-secondary dark:text-dark-content-secondary italic', className)}>
        {t('master_document.factBox.financial.pendingPosting')}
      </div>
    );
  }

  return (
    <div className={clsx('flex flex-col gap-0.5', className)}>
      {/* Colores semánticos: verde=cobrar, rojo=pagar. Migrar a tokens success/error cuando existan */}
      {hasReceivable && (
        <Row label={t('master_document.factBox.financial.receivables')} value={formatCurrency(data.receivableAmount!)} color="text-green-700 dark:text-green-400" />
      )}
      {hasPayable && (
        <Row label={t('master_document.factBox.financial.payables')} value={formatCurrency(data.payableAmount!)} color="text-error-content dark:text-error-content" />
      )}
      {hasAdvance && (
        <Row label={t('master_document.factBox.financial.crossedAdvances')} value={formatCurrency(data.advanceAmount!)} />
      )}
      {hasCash && (
        <Row label={t('master_document.factBox.financial.cash')} value={formatCurrency(data.cashAmount!)} />
      )}

      {/* Local currency equivalents */}
      {hasLocal && data.currencyCode && (
        <>
          <div className="h-px bg-border-secondary dark:bg-dark-border-primary my-1.5" />
          <span className="text-[10px] font-semibold text-content-tertiary dark:text-dark-content-secondary mb-0.5">
            {t('master_document.factBox.financial.localEquivalent')}
          </span>
          {data.receivableAmountLocal != null && data.receivableAmountLocal !== 0 && (
            <Row label={t('master_document.factBox.financial.localReceivables')} value={formatCurrency(data.receivableAmountLocal)} />
          )}
          {data.payableAmountLocal != null && data.payableAmountLocal !== 0 && (
            <Row label={t('master_document.factBox.financial.localPayables')} value={formatCurrency(data.payableAmountLocal)} />
          )}
        </>
      )}
    </div>
  );
}
